import moment from 'moment';
import * as Yup from 'yup';
import { find, isArray, isEmpty, isNil, keys, last, first, isNaN, trim, values, cloneDeep, uniq, has } from 'lodash';
import { isNode, type Node } from 'reactflow';
// Import Enums
import {
    PageTypesEnum,
    WhatsAppComponentTypeEnum,
    WhatsAppHeaderFormatEnum,
    WhatsAppTemplateCategoryEnum,
    WhatsAppTemplateStatusEnum,
    WhatsAppTemplateSubCategoryEnum,
    CJVisualizationUnlockConditionTypeEnum,
} from 'enums';
// Import Constants
import {
    l,
    MonthDayYearWithTimeFormat,
    MonthDayYearFormat,
    ZERO_TIME_WITHOUT_SECONDS,
    CustomerJourneyErrorCodes,
    CustomerJourneyGroupBlockTypes,
    CampaignStatusLabels,
    CJReferenceInArgumentType,
    CJFormulaInArgumentType,
    CJArgumentValuesTypes,
    DateToISOStringFormat,
    excludeRightValueContentVisibilitiesOperators,
    CJDependencyTypes,
    TemplateInputTypes,
    ExpirationTypes,
    ExpirationLabels,
    ExpirationBonusModelTypes,
    JSON_OBJECT,
} from 'constants/common';
import { formsTypes } from 'components/CustomerJourney/config';
import { dynamicDateData } from 'components/CustomerJourney/forms/CJDateTime/config';
// Import Services
import { isSchedulerUnitPairsInvalid } from 'services/schedular';
// Import Helpers
import {
    customMomentWithoutTimezoneConversion,
    customMomentWithTimezoneConversion,
    isOnlyMinus,
    calculateTemplateBodyLengthWithRegEx,
    getByteLen,
} from 'services/Helpers';
import {
    filterFlowNodes,
    getFormContent,
    getIncomers,
    getMatchedProperties,
    getModalCollectedProperties,
    getCJVisualizationProgressFormConfig,
    getCJVisualizationReferenceFormConfig,
    getCJVisualizationDueDateFormConfig,
    getCJVisualizationProgressOptions,
    isCJReferenceInArgumentValue,
} from 'services/customerJourney/helpers';
import { htmlStringToDomElement, recoverTopImageUrlPlaceholders } from 'services/template';
// Import Validation
import {
    CJBonusAmountInputValidationSchema,
    CJPartnerBonusIdValidationSchema,
    CJReferenceOptionValidationSchema,
    CJVisualizationStepTemplateValidationSchema,
    CJVisualizationStepIconValidationSchema,
    CJVisualizationButtonValidationSchema,
    CJBlockNameOptionValidationSchema,
    CJTemplateIdValidationSchema,
    CJBonusExpirationValidationSchema,
    CJBestMessageSenderValidationSchema,
    CJVisualizationProgressTitleValidationSchema,
} from './schemas.validation';
import { yupSchemaValidate } from './yupUtils';

interface ErrorMessage {
    errorMessage?: string;
    errorParams?: AnyObject;
    errorCode?: number;
}

const messageTypes = {
    BlockIsRequired: 'BlockIsRequired',
    PropertyIsRequired: 'PropertyIsRequired',
    BlockDoesNotExist: 'BlockDoesNotExist',
    BlockPropertyDoesNotExist: 'BlockPropertyDoesNotExist',
    MatchedBlockDoesNotVisible: 'MatchedBlockDoesNotVisible',
    MatchedBlockPropertyDoesNotVisible: 'MatchedBlockPropertyDoesNotVisible',
    BlockCollectedPropertiesNotExist: 'BlockCollectedPropertiesNotExist',
    ValueIsRequired: 'ValueIsRequired',
};

const templateSubjectValidationLabels = {
    containKPIVariableWarningMessage: l.SubjectContainKPIDynamicVariable,
    childDoesNotContainVariableWarningMessage: l.ChildSubjectDoesNotContainDynamicVariable,
    defaultDoesNotContainDynamicVariableError: l.DefaultSubjectDoesNotContainDynamicVariable,
    dynamicVariableWithUnknownLengthWarning: l.SubjectContainDynamicVariableWithUnknownLength,
    defaultContainsUnknownDynamicVariableWarning: l.DefaultSubjectContainUnknownDynamicVariable,
};

const templateBodyValidationLabels = {
    containKPIVariableWarningMessage: l.TemplateContainKPIDynamicVariable,
    childDoesNotContainVariableWarningMessage: l.ChildTemplateDoesNotContainDynamicVariable,
    defaultDoesNotContainDynamicVariableError: l.DefaultTemplateDoesNotContainDynamicVariable,
    dynamicVariableWithUnknownLengthWarning: l.TemplateContainDynamicVariableWithUnknownLength,
    dynamicVariableForbiddenErrorMessage: l.TemplateForbiddenDynamicVariableErrorMessage,
    defaultContainsUnknownDynamicVariableWarning: l.DefaultTemplateContainUnknownDynamicVariable,
};

const templateCascadeSMSBodyValidationLabels = {
    containKPIVariableWarningMessage: l.TemplateContainKPIDynamicVariableCascadeSMS,
    childDoesNotContainVariableWarningMessage: l.ChildTemplateDoesNotContainDynamicVariableCascadeSMS,
    defaultDoesNotContainDynamicVariableError: l.DefaultTemplateDoesNotContainDynamicVariableCascadeSMS,
    dynamicVariableWithUnknownLengthWarning: l.TemplateContainDynamicVariableWithUnknownLengthCascadeSMS,
    defaultContainsUnknownDynamicVariableWarning: l.DefaultTemplateContainUnknownDynamicVariableCascadeSMS,
};

const validationTypeLabels = {
    [TemplateInputTypes.Body]: templateBodyValidationLabels,
    [TemplateInputTypes.SMSBody]: templateCascadeSMSBodyValidationLabels,
    [TemplateInputTypes.Subject]: templateSubjectValidationLabels,
    [TemplateInputTypes.TitleOfPushNotification]: templateSubjectValidationLabels,
};

Yup.addMethod(
    Yup.object,
    'optionReference',
    function (elements, node, optionType, modelRelationScope, propertySettings, withoutType, message) {
        return this.test('optionReferenceTest', message, function (value) {
            const { path, createError } = this;
            if (!isNil(value)) {
                const { blockId, propertyName } = value;
                if (isNil(blockId)) {
                    return createError({ path, message, type: messageTypes.BlockIsRequired, params: { blockId } });
                }
                if (isNil(propertyName)) {
                    // case is not possible
                    return createError({
                        path,
                        message,
                        type: messageTypes.PropertyIsRequired,
                        params: { propertyName },
                    });
                }
                if (!isNil(blockId) && !isNil(propertyName)) {
                    const block = find(elements, (el) => {
                        return isNode(el) && el.id === blockId;
                    });
                    if (isNil(block?.data?.metaData?.Properties)) {
                        return createError({
                            path,
                            message,
                            type: messageTypes.BlockDoesNotExist,
                            params: { blockId },
                        });
                    } else {
                        let property;
                        if (!isNil(propertySettings?.typeName)) {
                            property = find(propertySettings.properties, (item) => item.Name === propertyName);
                        } else {
                            property = find(block.data.metaData.Properties, (item) => item.Name === propertyName);
                        }
                        if (isNil(property)) {
                            // case is not possible
                            return createError({
                                path,
                                message,
                                type: messageTypes.BlockPropertyDoesNotExist,
                                params: { propertyName, block },
                            });
                        } else {
                            if (!isNil(node?.data?.collectedProperties)) {
                                const collectedProperties = getModalCollectedProperties(modelRelationScope, node, {
                                    SelfModelTypeName: propertySettings?.typeName,
                                    SelfModelProperties: { Properties: propertySettings?.properties },
                                });
                                const matchedProperties = withoutType
                                    ? collectedProperties
                                    : getMatchedProperties(optionType, collectedProperties);
                                const matchedBlock: Nilable<AnyObject> = find(
                                    matchedProperties,
                                    (item: AnyObject) => item.id === blockId,
                                );
                                if (isNil(matchedBlock)) {
                                    return createError({
                                        path,
                                        message,
                                        type: messageTypes.MatchedBlockDoesNotVisible,
                                        params: { blockId, block },
                                    });
                                } else {
                                    const matchedProperty = find(
                                        matchedBlock.properties,
                                        (item) => item.Name === propertyName,
                                    );
                                    if (isNil(matchedProperty)) {
                                        // case is not possible
                                        return createError({
                                            path,
                                            message,
                                            type: messageTypes.MatchedBlockPropertyDoesNotVisible,
                                            params: { propertyName, block },
                                        });
                                    } else {
                                        // It's all success
                                    }
                                }
                            } else {
                                // case is not possible
                                return createError({
                                    path,
                                    message,
                                    type: messageTypes.BlockCollectedPropertiesNotExist,
                                    params: { blockId, block },
                                });
                            }
                        }
                    }
                }
            } else {
                return createError({ path, message, type: messageTypes.ValueIsRequired });
            }

            return true;
        });
    },
);

Yup.addMethod(Yup.object, 'blockIdValidation', function (elements, node, message) {
    return this.test('blockIdValidationTest', message, function (value) {
        const { path, createError } = this;
        if (!isNil(value)) {
            const { value: blockId } = value;
            if (isNil(blockId)) {
                return createError({ path, message, type: messageTypes.BlockIsRequired, params: { blockId } });
            } else {
                const block = find(elements, (el) => {
                    return isNode(el) && el.id === blockId;
                });
                if (isNil(block)) {
                    return createError({
                        path,
                        message,
                        type: messageTypes.BlockDoesNotExist,
                        params: { blockId },
                    });
                } else {
                    if (!isNil(node?.data?.collectedProperties)) {
                        const collectedBlocks = node.data.collectedProperties.map(({ id, name }) => ({
                            id,
                            name,
                        }));
                        const matchedBlock = find(collectedBlocks, (item) => item.id === blockId);
                        if (isNil(matchedBlock)) {
                            return createError({
                                path,
                                message,
                                type: messageTypes.MatchedBlockDoesNotVisible,
                                params: { blockId, block },
                            });
                        } else {
                            // It's all success
                        }
                    } else {
                        // case is not possible
                        return createError({
                            path,
                            message,
                            type: messageTypes.BlockCollectedPropertiesNotExist,
                            params: { blockId, block },
                        });
                    }
                }
            }
        } else {
            return createError({ path, message, type: messageTypes.ValueIsRequired });
        }

        return true;
    });
});

Yup.addMethod(Yup.string, 'negativeValueValidation', function (message = {}) {
    return this.test('negativeValueValidationTest', message, function (value) {
        if (isOnlyMinus(value)) {
            const { path, createError } = this;
            return createError({
                path: path,
                message: {
                    label: l.CannotBeOnlyMinus,
                    fieldName: l.Value,
                    ...message,
                },
            });
        }
        return true;
    });
});

Yup.addMethod(Yup.mixed, 'maxValueValidation', function (validation, message = {}) {
    return this.test('maxValueValidationTest', message, function (value) {
        if (!isNil(validation?.Value) && !isNil(value)) {
            const { path, createError } = this;
            const isIncluded = validation.IsIncluded ?? false;
            const hasError = isIncluded ? +value > +validation.Value : +value >= +validation.Value;
            if (hasError) {
                return createError({
                    path: path,
                    message: {
                        label: isIncluded ? l.MustBeSmallerOrEqualThan : l.MustBeSmallerThan,
                        fieldName: l.Value,
                        count: validation.Value.toString(),
                        ...message,
                    },
                });
            }
        }
        return true;
    });
});
Yup.addMethod(Yup.mixed, 'minValueValidation', function (validation, message = {}) {
    return this.test('minValueValidationTest', message, function (value) {
        if (!isNil(validation?.Value) && !isNil(value)) {
            const { path, createError } = this;
            const isIncluded = validation.IsIncluded ?? false;
            const hasError = isIncluded ? +value < +validation.Value : +value <= +validation.Value;
            if (hasError) {
                return createError({
                    path: path,
                    message: {
                        label: isIncluded ? l.MustBeBiggerOrEqualThan : l.MustBeBiggerThan,
                        fieldName: l.Amount,
                        count: validation.Value.toString(),
                        ...message,
                    },
                });
            }
        }
        return true;
    });
});
Yup.addMethod(Yup.mixed, 'decimalPointsValidation', function (validation, message = {}) {
    return this.test('decimalPointsValidationTest', message, function (value) {
        if (!isNil(validation) && validation > 0 && !isNil(value) && !isNaN(+value)) {
            const { path, createError } = this;
            const splitValue = (+value).toString().split('.'); // value can be string also number
            if (
                (isNil(splitValue[1]) && splitValue[0].includes('e')) ||
                (!isNil(splitValue[1]) && splitValue[1].length > validation)
            ) {
                return createError({
                    path: path,
                    message: {
                        label: l.MaximumNumberDecimalDigit,
                        fieldName: l.Value,
                        count: validation.toString(),
                        ...message,
                    },
                });
            }
        }
        return true;
    });
});

Yup.addMethod(Yup.mixed, 'stringPatternValidation', function (validation, message = {}) {
    return this.test('stringPatternValidationTest', message, function (value) {
        if (!isNil(validation) && !isNil(value)) {
            const { path, createError } = this;
            if (isNil(value.toString().match(validation))) {
                return createError({
                    path: path,
                    message: { label: l.PatternDoesNotMatch, ...message },
                });
            }
        }
        return true;
    });
});

Yup.addMethod(Yup.mixed, 'maxLengthValidation', function (validation, message = {}) {
    return this.test('maxLengthValueValidationTest', message, function (value) {
        if (!isNil(validation?.Value) && !isNil(value) && isArray(value)) {
            const { path, createError } = this;
            const isIncluded = validation.IsIncluded ?? false;
            const hasError = isIncluded ? value.length > +validation.Value : value.length >= +validation.Value;
            if (hasError) {
                return createError({
                    path: path,
                    message: {
                        label: isIncluded ? l.MustBeLessOrEqualThan : l.MustBeLessThan,
                        fieldName: l.Value,
                        length: validation.Value.toString(),
                        ...message,
                    },
                });
            }
        }
        return true;
    });
});

Yup.addMethod(Yup.mixed, 'startDateCJScheduleValidation', function (t, elements, message = {}) {
    return this.test('startDateCJScheduleValidationTest', message, function (value) {
        const { path, createError } = this;
        const { timeZone, autoStartTime, startTime } = this.parent;
        const validationResult = validateCJSchedule(
            value,
            timeZone,
            elements,
            autoStartTime ?? startTime ?? ZERO_TIME_WITHOUT_SECONDS,
            (optionStartDate, _optionEndDate, dateValue, node) => {
                return node?.data?.isEditOnlyLive !== true && optionStartDate <= dateValue;
            },
        );
        if (!isNil(validationResult)) {
            return createError({
                path: path,
                message: t(l.StartDateMustBeSmallerThenBlockDate, {
                    optionName: t(validationResult.option?.CustomAttributes?.DisplayName),
                    blockName: validationResult.node.data.name,
                }),
            });
        }
        return true;
    });
});

Yup.addMethod(Yup.mixed, 'endDateCJScheduleValidation', function (t, elements, message = {}) {
    return this.test('endDateCJScheduleValidationTest', message, function (value) {
        const { path, createError } = this;
        const { timeZone, autoEndTime, endTime } = this.parent;
        const validationResult = validateCJSchedule(
            value,
            timeZone,
            elements,
            autoEndTime ?? endTime ?? ZERO_TIME_WITHOUT_SECONDS,
            (_optionStartDate, optionEndDate, dateValue) => {
                return optionEndDate > dateValue;
            },
        );
        if (!isNil(validationResult)) {
            return createError({
                path: path,
                message: t(l.EndDateMustBeBiggerThenBlockDate, {
                    optionName: t(validationResult.option?.CustomAttributes?.DisplayName),
                    blockName: validationResult.node.data.name,
                }),
            });
        }
        return true;
    });
});

function validateCJSchedule(value, timeZone, elements, selectedTime, comparisonHasError) {
    if (!isNil(value) && !isEmpty(elements)) {
        const blocks = elements.filter((el) => {
            return isNode(el);
        });

        const toTimezoneOffset = (zone) => {
            if (isNil(zone)) return 0;
            if (typeof zone === 'number' && !Number.isNaN(zone)) return zone;

            const numericValue = Number(zone);
            if (!Number.isNaN(numericValue)) return numericValue;

            if (typeof zone === 'string') {
                const match = zone.match(/([+-]?\d{1,2})(?::(\d{2}))?/);
                if (!isNil(match)) {
                    const sign = match[1].startsWith('-') ? -1 : 1;
                    const hour = Math.abs(parseInt(match[1], 10));
                    const minutes = parseInt(match[2] ?? '0', 10);
                    return sign * (hour + minutes / 60);
                }
            }

            return 0;
        };

        const buildTimestamp = (dateValue, timeValue, zone) => {
            const normalizedDate = (() => {
                if (dateValue instanceof Date) {
                    return moment(dateValue).format(MonthDayYearFormat);
                }

                const parsedValue = moment(
                    dateValue,
                    [MonthDayYearFormat, MonthDayYearWithTimeFormat, 'DD.MM.YYYY', moment.ISO_8601],
                    true,
                );

                return parsedValue.isValid()
                    ? parsedValue.format(MonthDayYearFormat)
                    : customMomentWithoutTimezoneConversion(dateValue).format(MonthDayYearFormat);
            })();
            const normalizedTime = timeValue ?? ZERO_TIME_WITHOUT_SECONDS;
            return customMomentWithTimezoneConversion(
                moment(`${normalizedDate} ${normalizedTime}`, MonthDayYearWithTimeFormat, true),
                toTimezoneOffset(zone),
                true,
            ).valueOf();
        };

        for (let blockIndex = 0; blockIndex < blocks.length; blockIndex++) {
            const node = blocks[blockIndex];
            if (!isNil(node?.data?.metaData?.Options)) {
                for (let optionIndex = 0; optionIndex < node?.data?.metaData?.Options.length; optionIndex++) {
                    const option = node?.data?.metaData?.Options[optionIndex];
                    const config = getFormContent(option.BaseTypes, formsTypes);
                    if (config.optionType === formsTypes.WfSchedule.optionType) {
                        const val = node?.data?.apiModel[option.Name];
                        const uiModel = config.mapToUIModel(val);
                        if (
                            !isNil(uiModel) &&
                            uiModel.$type !== CJArgumentValuesTypes.DefaultInArgument &&
                            uiModel.$type !== CJArgumentValuesTypes.ReferenceInArgument &&
                            uiModel.$type !== CJArgumentValuesTypes.FormulaInArgument &&
                            uiModel.isActiveSchedule === true
                        ) {
                            const optionStartDate = buildTimestamp(
                                uiModel.startDate,
                                uiModel.startTime,
                                uiModel.timeZone,
                            );
                            const optionEndDate = !isNil(val?.EndDate)
                                ? buildTimestamp(
                                      uiModel.endDate,
                                      uiModel.endTime ?? uiModel.startTime,
                                      uiModel.timeZone,
                                  )
                                : optionStartDate;
                            const dateValue = buildTimestamp(value, selectedTime, timeZone);

                            if (comparisonHasError(optionStartDate, optionEndDate, dateValue, node)) {
                                return { node: node, option: option };
                            }
                        }
                    }
                }
            }
        }
        return null;
    }
}

function isRequired(value) {
    return !value;
}

function isInRange(
    value: string | number,
    { range = [], ignoreTypes = true }: { range?: (string | number)[]; ignoreTypes?: boolean },
) {
    if (!Array.isArray(range)) {
        throw new TypeError("Expected 'range' to be an array");
    }

    if (ignoreTypes) {
        return !range.map(Number).includes(Number(value));
    }

    return !range.includes(value);
}

function isFileFormatValid(value, format) {
    return !format.split(',').includes(value.split('.').pop());
}

function isRequiredEditor(value) {
    if (!isNil(value)) {
        return value.trim() === '';
    }

    return true;
}

function isRequiredEmailBuilder(value) {
    return value === '' || value === '<body id="wrapper" style="box-sizing: border-box; margin: 0;"></body>';
}

function isLessThen(value, count) {
    return value.length < count;
}

function isMoreThen(value, count) {
    return value?.length > count;
}

function viberButtonTextMaxLength(value, count) {
    const { text } = value;
    return text?.length > count;
}

function telegramButtonTextMaxLength(value, count) {
    const { text } = value;
    return text?.length > count;
}

function whatsAppProviderButtonTextMaxLength(value, count) {
    const { text } = value;
    return text?.length > count;
}

const whatsAppProviderButtonTextValidation = (data) => {
    const { text, url } = data;
    return isEmpty(text.trim()) && !isEmpty(url.trim());
};

const whatsAppProviderButtonUrlValidation = (data) => {
    const { text, url } = data;
    return isEmpty(url.trim()) && !isEmpty(text.trim());
};

function isMoreThanRegex(value, { count, regExpConfig, fieldName }) {
    const result = calculateTemplateBodyLengthWithRegEx(regExpConfig, value);
    return getByteLen(result) > count
        ? [
              {
                  errorMessage: l.MustContainMaximumBytes,
                  errorParams: { count: count.toString(), fieldName: fieldName },
              },
          ]
        : false;
}

const customerJourneyLimitValidation = ({ limit, count }) => {
    return {
        errorMessages: [],
        warningMessages: [
            {
                errorMessage: l.CustomerJourneyLimitWarningMessage,
                errorParams: {
                    limit: limit.toString(),
                    count: count.toString(),
                },
            },
        ],
    };
};

function regexMatching(value, regex) {
    return value.match(new RegExp(regex)) === null;
}

function isGreaterThan(value, count) {
    return +value <= +count;
}

function isGreaterThanOrEqual(value, count) {
    return +value < +count;
}

function isLessThan(value, count) {
    return +value >= +count;
}

function isLessThanOrEqual(value, count) {
    return +value > +count;
}

function isEmptyValue(value) {
    return isEmpty(value);
}

function isBeforeCurrentDate(
    date: Nilable<string | number | Date | moment.Moment>,
    time: string,
    timeZone: number | string,
) {
    if (!moment(date).isValid()) {
        return true;
    }

    const chosenMoment = customMomentWithTimezoneConversion(
        moment(`${moment(date).format(MonthDayYearFormat)} ${time}`, MonthDayYearWithTimeFormat),
        timeZone,
        true,
    );

    return chosenMoment.isBefore(moment());
}

function isDateSmallerThanDate(fromDate, toDate) {
    const chosenStartDate = new Date(moment(fromDate).format(MonthDayYearFormat));
    const chosenEndDate = new Date(moment(toDate).format(MonthDayYearFormat));

    return chosenStartDate.getTime() > chosenEndDate.getTime();
}

const getDuplicateNodeIds = (nodeElements: Node[]): Set<string> => {
    const idCountByNode: Record<string, number> = {};

    nodeElements.forEach(({ id }) => {
        idCountByNode[id] = (idCountByNode[id] ?? 0) + 1;
    });

    return new Set(keys(idCountByNode).filter((id) => idCountByNode[id] > 1));
};

const {
    minimumTwoValidBlocksWithValidConnection,
    leastOneTargetBlock,
    allBlocksMustBeConnected,
    targetHasNotInput,
    blockInvalid,
    blockIDMustBeUnique,
} = CustomerJourneyErrorCodes;

function isValidCustomerJourney(elements) {
    const errorMessages: ErrorMessage[] = [];

    if (isNil(elements) || !isArray(elements) || isEmpty(elements)) {
        errorMessages.push({ errorCode: minimumTwoValidBlocksWithValidConnection.code });
    } else {
        const nodeElements = filterFlowNodes(elements);

        if (isEmpty(nodeElements)) {
            errorMessages.push({ errorCode: minimumTwoValidBlocksWithValidConnection.code });
        } else {
            const duplicateNodeIds = getDuplicateNodeIds(nodeElements);
            const targetBlocks = nodeElements.filter(({ type }) => type === CustomerJourneyGroupBlockTypes.target);

            if (targetBlocks.length === 0 || targetBlocks.length > 1) {
                errorMessages.push({ errorCode: leastOneTargetBlock.code, errorParams: {} });
            }

            targetBlocks.forEach((target) => {
                const { data } = target;
                data.errorMessages = [];

                if (duplicateNodeIds.has(target.id)) {
                    data.errorMessages.push({ errorCode: blockIDMustBeUnique.code });
                }

                const incomers = getIncomers(target, elements);
                if (incomers.length > 0) {
                    data.errorMessages.push({ errorCode: targetHasNotInput.code });
                    errorMessages.push({ errorCode: targetHasNotInput.code, errorParams: {} });
                }
                customerJourneyNameValidation(target);
                customerJourneyOptionsValidation(target, elements);

                data.isValid = data.errorMessages.length === 0;
                if (!data.isValid) {
                    errorMessages.push({
                        errorCode: blockInvalid.code,
                        errorParams: { blockName: { isTranslated: true, value: target?.data?.name } },
                    });
                }
            });

            const nonTargetBlocks = nodeElements.filter(({ type }) => type !== CustomerJourneyGroupBlockTypes.target);

            if (nonTargetBlocks.length === 0) {
                errorMessages.push({ errorCode: minimumTwoValidBlocksWithValidConnection.code });
            } else {
                nonTargetBlocks.forEach((node) => {
                    const { data } = node;
                    data.errorMessages = [];

                    if (duplicateNodeIds.has(node.id)) {
                        data.errorMessages.push({ errorCode: blockIDMustBeUnique.code });
                    }

                    const incomers = getIncomers(node, elements);
                    if (incomers.length === 0) {
                        // TODO when nonTargetBlocks must be have input connection
                        data.errorMessages.push({ errorCode: allBlocksMustBeConnected.code });
                    }

                    customerJourneyNameValidation(node);
                    customerJourneyOptionsValidation(node, elements);

                    data.isValid = data.errorMessages.length === 0;
                    if (!data.isValid) {
                        errorMessages.push({
                            errorCode: blockInvalid.code,
                            errorParams: { blockName: { isTranslated: true, value: node?.data?.name } },
                        });
                    }
                });
            }
        }
    }
    return errorMessages.length > 0 ? errorMessages : false;
}

interface IsAvailableCustomerJourneyBlocksParams {
    elements: Node[];
    availableBlocks: string[];
}

interface IsAvailablePushNotificationRouteParams {
    value: string;
    availableRoutes: string[];
}

const isAvailableCustomerJourneyBlocks = ({ elements, availableBlocks }: IsAvailableCustomerJourneyBlocksParams) => {
    const { unavailableBlockError } = CustomerJourneyErrorCodes;
    const errorMessages: { errorCode: number }[] = [];
    if (isNil(elements) || !isArray(elements) || isEmpty(elements) || isEmpty(availableBlocks)) {
        return false;
    }

    const nodeElements = filterFlowNodes(elements);

    nodeElements.forEach((node) => {
        const { data } = node;
        const blockName = data.metaData?.Name;
        if (!availableBlocks.includes(blockName)) {
            data.isValid = false;
            data.errorMessages.push({ errorMessage: unavailableBlockError.label });
            errorMessages.push({ errorCode: unavailableBlockError.code });
        }
    });

    return errorMessages.length > 0 ? errorMessages : false;
};

const isAvailablePushNotificationRoute = ({ value, availableRoutes }: IsAvailablePushNotificationRouteParams) => {
    if (isNil(value) || !isArray(availableRoutes) || isEmpty(availableRoutes)) {
        return true;
    }

    return !availableRoutes.includes(value);
};

function getTemplateUsageDynamicValues(dynamicValues, element) {
    return !isEmpty(element)
        ? keys(dynamicValues).reduce((acc, key) => {
              if (element.includes(`{${key}}`)) {
                  acc[key] = dynamicValues[key];
              }
              return acc;
          }, {})
        : {};
}

function getFilteredDynamicValues(allPlaceholders, bodyWithRegEx) {
    const allDynamicValues = [...bodyWithRegEx.matchAll(/{(?<dynamicValue>[a-zA-Z]\w*)}/gm)].map(
        (value) => value.groups.dynamicValue,
    );

    return allDynamicValues.reduce(
        (acc, value) => {
            if (isNil(allPlaceholders[value])) {
                acc.unknownDynamicValues.push(value);
            } else {
                acc.dynamicValuesWithUnknownLength.push(allPlaceholders[value]);
            }

            return acc;
        },
        {
            dynamicValuesWithUnknownLength: [],
            unknownDynamicValues: [],
        },
    );
}

function getAllDynamicVariables(text: string) {
    const regex = /\{([A-Z][^}]*)\}/g;
    const matches = [...text.matchAll(regex)];

    return matches.map((match) => match[1]);
}

function doCrossValidation(value, parameter) {
    const errorMessages: ErrorMessage[] = [];
    const warningMessages: ErrorMessage[] = [];

    if (isEmpty(value?.dynamicValues) || isEmpty(value?.data)) {
        return { errorMessages: errorMessages, warningMessages: warningMessages };
    }

    const { data, dynamicValues, shortenerAndTrackerSettings, partnerLanguages } = value;
    const allPlaceholders = dynamicValues.reduce((acc, value) => {
        acc[value.FieldName] = value;
        return acc;
    }, {});

    const defaultPlaceholders: AnyObject = getTemplateUsageDynamicValues(allPlaceholders, data?.defaultLang[parameter]);

    const {
        containKPIVariableWarningMessage,
        childDoesNotContainVariableWarningMessage,
        defaultDoesNotContainDynamicVariableError,
        dynamicVariableWithUnknownLengthWarning,
        defaultContainsUnknownDynamicVariableWarning,
    } = validationTypeLabels[parameter];

    values(defaultPlaceholders).forEach((item) => {
        if (!isNil(item.ColumnInfoId)) {
            warningMessages.push({
                errorMessage: containKPIVariableWarningMessage,
                errorParams: {
                    dynamicVariable: item.DisplayNameKey,
                },
            });
        }
    });

    values(data).forEach((template) => {
        if (!isNil(template?.Language)) {
            const tmpPlaceholders = getTemplateUsageDynamicValues(allPlaceholders, template[parameter]);

            keys(defaultPlaceholders).forEach((key) => {
                if (isNil(tmpPlaceholders[key])) {
                    warningMessages.push({
                        errorMessage: childDoesNotContainVariableWarningMessage,
                        errorParams: {
                            dynamicVariable: defaultPlaceholders[key].DisplayNameKey,
                            language: partnerLanguages[template.Language]?.Name,
                        },
                    });
                }
            });

            keys(tmpPlaceholders).forEach((key) => {
                if (isNil(defaultPlaceholders[key])) {
                    warningMessages.push({
                        errorMessage: defaultDoesNotContainDynamicVariableError,
                        errorParams: {
                            dynamicVariable: tmpPlaceholders[key].DisplayNameKey,
                            language: partnerLanguages[template.Language]?.Name,
                        },
                    });

                    if (!isNil(tmpPlaceholders[key].ColumnInfoId)) {
                        errorMessages.push({
                            errorMessage: defaultDoesNotContainDynamicVariableError,
                            errorParams: {
                                dynamicVariable: tmpPlaceholders[key].DisplayNameKey,
                                language: partnerLanguages[template.Language]?.Name,
                            },
                        });
                    }
                }
            });
        }
    });

    values(data).forEach((template) => {
        const element = [TemplateInputTypes.Body, TemplateInputTypes.SMSBody].includes(parameter)
            ? calculateTemplateBodyLengthWithRegEx(shortenerAndTrackerSettings, cloneDeep(template[parameter]))
            : cloneDeep(template[parameter]);
        const { dynamicValuesWithUnknownLength, unknownDynamicValues } = getFilteredDynamicValues(
            allPlaceholders,
            element,
        );

        if (!isEmpty(dynamicValuesWithUnknownLength)) {
            dynamicValuesWithUnknownLength.forEach((value) => {
                warningMessages.push({
                    errorMessage: dynamicVariableWithUnknownLengthWarning,
                    errorParams: {
                        dynamicVariable: value.DisplayNameKey,
                        language: partnerLanguages[template.Language]?.Name ?? l.Default,
                    },
                });
            });
        }

        if (isNil(template.Language) && !isEmpty(unknownDynamicValues)) {
            unknownDynamicValues.forEach((value) => {
                warningMessages.push({
                    errorMessage: defaultContainsUnknownDynamicVariableWarning,
                    errorParams: {
                        dynamicVariable: value,
                    },
                });
            });
        }
    });

    return { errorMessages: errorMessages, warningMessages: warningMessages };
}

function doForbiddenDynamicVariablesValidation(value, parameter) {
    const errorMessages: ErrorMessage[] = [];
    const { templateData, dynamicVariables, allowedDynamicVariables, partnerLanguages, templateType } = value;

    if (isEmpty(dynamicVariables) || isEmpty(templateData) || isEmpty(allowedDynamicVariables)) {
        return { errorMessages: errorMessages };
    }

    const dynamicVariablesIdNames = dynamicVariables.reduce((acc, item) => {
        acc[item.channels[templateType].FieldName] = item.channels[templateType].Id;

        return acc;
    }, {});

    keys(templateData).forEach((key) => {
        const body = templateData[key][parameter] ?? '';
        const parsedBody = recoverTopImageUrlPlaceholders(body, dynamicVariables, templateType);
        const allDynamicValues = getAllDynamicVariables(parsedBody);

        const forbiddenDynamicValues = allDynamicValues
            .filter(
                (item) =>
                    !isNil(dynamicVariablesIdNames[item]) &&
                    !allowedDynamicVariables.includes(dynamicVariablesIdNames[item]),
            )
            .map((item) => `{${item}}`);

        if (!isEmpty(forbiddenDynamicValues)) {
            errorMessages.push({
                errorMessage: l.TemplateForbiddenDynamicVariableErrorMessage,
                errorParams: {
                    dynamicVariable: forbiddenDynamicValues.join(', '),
                    language: partnerLanguages[templateData[key].Language]?.Name ?? l.Default,
                },
            });
        }
    });

    return { errorMessages: errorMessages };
}

const templateForbiddenDynamicVariables = (value, params) => {
    let errorMessages: ErrorMessage[] = [];

    params.forEach((parameter) => {
        const tmpResult = doForbiddenDynamicVariablesValidation(value, parameter);

        errorMessages = [...errorMessages, ...tmpResult.errorMessages];
    });

    return { errorMessages: errorMessages };
};

const whatsAppPlaceholderMappingRequired = (value) => {
    if (isNil(value)) {
        return true;
    }

    const { requiredPlaceholders, mapping } = value ?? {};

    if (isEmpty(requiredPlaceholders)) {
        return false;
    }

    if (isNil(mapping)) {
        return true;
    }

    return requiredPlaceholders.some(
        ({ componentKey, paramIndex }) =>
            !(mapping?.[componentKey]?.[paramIndex]?.dynamicVariableFieldName ?? '').toString().trim(),
    );
};

const externalWhatsAppTemplateDetails = (value) => {
    if (isNil(value)) {
        return { errorMessages: [] };
    }

    const { status, category, subCategory, components } = value ?? {};

    if (status !== WhatsAppTemplateStatusEnum.Approved) {
        return { errorMessages: [{ errorMessage: l.ExternalWhatsAppTemplateStatusNotApproved }] };
    }
    if (category !== WhatsAppTemplateCategoryEnum.Marketing) {
        return { errorMessages: [{ errorMessage: l.ExternalWhatsAppTemplateCategoryNotMarketing }] };
    }
    if (subCategory !== null && subCategory !== WhatsAppTemplateSubCategoryEnum.Custom) {
        return { errorMessages: [{ errorMessage: l.ExternalWhatsAppTemplateSubCategoryInvalid }] };
    }
    const headerComponent = find(components, (component) => component?.Type === WhatsAppComponentTypeEnum.Header);
    if (
        headerComponent &&
        ![WhatsAppHeaderFormatEnum.Text, WhatsAppHeaderFormatEnum.Image].includes(headerComponent.Format)
    ) {
        return { errorMessages: [{ errorMessage: l.ExternalWhatsAppTemplateHeaderFormatUnsupported }] };
    }
    const buttonsComponent = find(components, (component) => component?.Type === WhatsAppComponentTypeEnum.Buttons);
    if (buttonsComponent?.Buttons?.some((button) => !isEmpty(button.Example))) {
        return { errorMessages: [{ errorMessage: l.ExternalWhatsAppTemplateButtonDynamicUrlUnsupported }] };
    }

    return { errorMessages: [] };
};

const templateCrossValidation = (value, params) => {
    let errorMessages: ErrorMessage[] = [];
    let warningMessages: ErrorMessage[] = [];

    params.forEach((parameter) => {
        const tmpResult = doCrossValidation(value, parameter);

        errorMessages = [...errorMessages, ...tmpResult.errorMessages];
        warningMessages = [...warningMessages, ...tmpResult.warningMessages];
    });
    return { errorMessages: errorMessages, warningMessages: warningMessages };
};

function customerJourneyNameValidation(node) {
    // TODO: name must be uniq, case is prevented in block name on change but full validation need checking
    if (!isNil(node?.data)) {
        const { data } = node;
        if (isNil(data.name) || isEmpty(data.name)) {
            data.errorMessages.push({ errorCode: CustomerJourneyErrorCodes.blockNameCanNotBeEmpty.code });
        }
    }
}

function cjStaticSchemaErrorMessageCreator(node) {
    return (e) => {
        if (!isNil(e?.message?.label)) {
            node.data.errorMessages.push({
                errorMessage: e.message?.label,
                errorParams: e.message,
            });
        } else {
            // eslint-disable-next-line no-console
            console.log(e?.message, e?.type, e?.params);
            node.data.errorMessages.push({
                errorMessage: l.SomethingIsWrong,
            });
        }
    };
}

export interface ReferencePropertyErrorMessage {
    errorMessage: string;
    errorParams?: { fieldName?: string; blockName?: string };
}

const getReferencePropertyErrorMessage = (e?: AnyObject): ReferencePropertyErrorMessage => {
    if (!isNil(e?.type)) {
        switch (e?.type) {
            case messageTypes.BlockIsRequired:
                return {
                    errorMessage: l.CannotBeEmpty,
                    errorParams: { fieldName: l.Block },
                };
            case messageTypes.PropertyIsRequired: // case does not possible
                return {
                    errorMessage: l.CannotBeEmpty,
                    errorParams: { fieldName: l.Property },
                };

            case messageTypes.BlockDoesNotExist:
                return {
                    errorMessage: l.BlockDoesNotExist,
                };
            case messageTypes.BlockPropertyDoesNotExist: // case does not possible
                return {
                    errorMessage: l.BlockPropertyDoesNotExist,
                };
            case messageTypes.MatchedBlockDoesNotVisible:
                return {
                    errorMessage: l.MatchedBlockDoesNotVisible,
                    errorParams: { blockName: e?.params?.block?.data?.name },
                };

            case messageTypes.MatchedBlockPropertyDoesNotVisible: // case does not possible
                return {
                    errorMessage: l.MatchedBlockPropertyDoesNotVisible,
                };

            case messageTypes.BlockCollectedPropertiesNotExist: // case does not possible
                return {
                    errorMessage: l.BlockCollectedPropertiesNotExist,
                };

            default:
                // case does not possible
                return {
                    errorMessage: l.SomethingIsWrong,
                };
        }
    } else {
        // case does not possible
        return {
            errorMessage: l.SomethingIsWrong,
        };
    }
};

function cjReferencePropertySchemaErrorMessageCreator(node) {
    return (e) => {
        node.data.errorMessages.push(getReferencePropertyErrorMessage(e));
    };
}

// yupSchemaValidate moved to yupUtils.ts to break circular dependency

function cjVisualizationConditionValidation(data, elements) {
    data.node.data.errorMessages = [];
    cjFilterConditionValidation(elements, data.node, data.option, formsTypes.FilterCondition);
    return data.node.data.errorMessages.length === 0;
}

function cjVisualizationTemplatesValidation(templateData, schemaCreator = CJVisualizationStepTemplateValidationSchema) {
    let isValid = true;
    values(templateData).forEach((item) => {
        item.isValid = yupSchemaValidate(schemaCreator(), item);
        isValid = isValid && item.isValid;
    });
    return isValid;
}

function cjVisualizationStepValidation(stepData, elements, isTarget = false, isTemplatesValidationSkipped = false) {
    stepData.isValid = cjVisualizationDueDateValidation(stepData, elements);
    if (isTarget) {
        stepData.isValid = cjVisualizationConditionValidation(stepData, elements) && stepData.isValid;
    }

    stepData.isValid =
        (isTemplatesValidationSkipped || cjVisualizationTemplatesValidation(stepData.templates)) && stepData.isValid;

    if (!isTarget) {
        stepData.isValid =
            stepData.isValid && yupSchemaValidate(CJVisualizationStepIconValidationSchema(), { icon: stepData.icon });

        stepData.subSteps.forEach((subStep) => {
            const sectionsValidations = [
                cjVisualizationConditionValidation(subStep, elements),
                cjVisualizationTemplatesValidation(subStep.templates),
                cjVisualizationSubStepRewardValidation(subStep, elements),
                cjVisualizationSubStepButtonValidation(subStep),
                cjVisualizationDueDateValidation(subStep, elements),
                cjVisualizationSubStepProgressValidation(subStep, elements),
            ];
            subStep.isValid = sectionsValidations.every(Boolean);
            stepData.isValid = stepData.isValid && subStep.isValid;
        });
    }
}

// Progress: title is required, current is a required reference, goal is optional (reference or static value);
// values are validated with the CJ option validation of the progress type form config (see helpers)
function cjVisualizationSubStepProgressValidation(subStep, elements) {
    const progress = subStep?.progress;
    if (isNil(progress)) {
        return true;
    }
    if (progress.showProgress !== true) {
        progress.isValid = true;
        return true;
    }

    const formConfig = getCJVisualizationProgressFormConfig(progress.type);
    const isCurrentReference = isCJReferenceInArgumentValue(progress.current);

    if (isNil(formConfig) || !isCurrentReference || isNil(subStep?.node) || isNil(subStep?.progressOption)) {
        progress.isValid = false;
        return false;
    }

    const isTitleValid = cjVisualizationTemplatesValidation(
        progress.templates,
        CJVisualizationProgressTitleValidationSchema,
    );
    const { node } = subStep;
    const { currentOption, goalOption } = getCJVisualizationProgressOptions(subStep.progressOption, formConfig);
    node.data.errorMessages = [];

    node.data.apiModel[currentOption.Name] = progress.current;
    optionApiModelValueValidation(progress.current, formConfig, currentOption, node, elements);

    if (!isNil(progress.goal)) {
        node.data.apiModel[goalOption.Name] = progress.goal;
        optionApiModelValueValidation(progress.goal, formConfig, goalOption, node, elements);
    }

    progress.isValid = isTitleValid && node.data.errorMessages.length === 0;
    return progress.isValid;
}

// Due date of the general details, a group or a step: the value is validated with the CJ option validation of its
// form config (see helpers), Dynamic Date is a WfDateTime value or reference, Duration must be a WfRemainingTime reference
function cjVisualizationDueDateValidation(item, elements) {
    const dueDate = item?.dueDate;
    if (isNil(dueDate)) {
        return true;
    }
    if (dueDate.isEnabled !== true) {
        dueDate.isValid = true;
        return true;
    }

    const formConfig = getCJVisualizationDueDateFormConfig(dueDate.type);
    const isReferenceMissing = formConfig?.isReferenceOnly === true && !isCJReferenceInArgumentValue(dueDate.value);

    if (
        isNil(formConfig) ||
        isNil(dueDate.value) ||
        isReferenceMissing ||
        isNil(item?.node) ||
        isNil(item?.dueDateOption)
    ) {
        dueDate.isValid = false;
        return false;
    }

    const option = { ...item.dueDateOption, BaseTypes: [formConfig.optionType] };
    item.node.data.errorMessages = [];
    item.node.data.apiModel[option.Name] = dueDate.value;
    optionApiModelValueValidation(dueDate.value, formConfig, option, item.node, elements);
    dueDate.isValid = item.node.data.errorMessages.length === 0;
    return dueDate.isValid;
}

// Validates the unlock condition of a step (sub step); availableStepUids are the uids of the steps that can unlock it
function cjVisualizationSubStepUnlockValidation(subStep, elements, availableStepUids: string[]) {
    const unlock = subStep?.unlock;
    if (isNil(unlock)) {
        return true;
    }
    if (unlock.isLocked !== true) {
        // unlocked step: unlock configuration is not validated and is not sent to the backend
        unlock.isValid = true;
        return true;
    }
    let isValid = false;
    switch (unlock.type) {
        case CJVisualizationUnlockConditionTypeEnum.PassSelectedStep:
            isValid = !isNil(unlock.stepUid) && availableStepUids.includes(unlock.stepUid);
            break;
        case CJVisualizationUnlockConditionTypeEnum.CJCondition:
            isValid = isNil(subStep?.unlockOption)
                ? true
                : cjVisualizationConditionValidation({ node: subStep.node, option: subStep.unlockOption }, elements);
            unlock.condition = { ...(unlock.condition ?? {}), isValid };
            break;
        case CJVisualizationUnlockConditionTypeEnum.DateTime:
            isValid = !isEmpty(unlock.dateTime) && moment(unlock.dateTime, DateToISOStringFormat).isValid();
            break;
        default:
            isValid = false;
    }
    unlock.isValid = isValid;
    subStep.isValid = subStep.isValid && isValid;
    return isValid;
}

function cjVisualizationSubStepButtonValidation(subStep) {
    const button = subStep?.button;
    if (isNil(button)) {
        return true;
    }
    if (button.showButton !== true) {
        // hidden button is not validated and is not sent to the backend
        button.isValid = true;
        return true;
    }
    button.isValid =
        cjVisualizationTemplatesValidation(button.templates, CJVisualizationButtonValidationSchema) &&
        !isNil(button.route?.page);
    return button.isValid;
}

function cjVisualizationSubStepRewardValidation(subStep, elements) {
    const reward = subStep?.reward;
    if (isNil(reward)) {
        return true;
    }
    if (reward.showReward !== true) {
        // hidden reward is not validated and is not sent to the backend
        reward.isValid = true;
        return true;
    }
    const isConditionValid = isNil(subStep?.rewardOption)
        ? true
        : cjVisualizationConditionValidation({ node: subStep.node, option: subStep.rewardOption }, elements);
    reward.condition = { ...(reward.condition ?? {}), isValid: isConditionValid };

    // client bonus id is a reference (required only when the option says so) validated with the CJ reference validation
    let isClientBonusIdValid = true;
    const rewardClientBonusIdOption = subStep?.rewardClientBonusIdOption;
    if (!isNil(rewardClientBonusIdOption) && !isNil(subStep?.node)) {
        const clientBonusId = reward.clientBonusId;
        if (isNil(clientBonusId)) {
            isClientBonusIdValid = rewardClientBonusIdOption.Required !== true;
        } else {
            const isReference = isCJReferenceInArgumentValue(clientBonusId);
            subStep.node.data.errorMessages = [];
            subStep.node.data.apiModel[rewardClientBonusIdOption.Name] = clientBonusId;
            if (isReference) {
                optionApiModelValueValidation(
                    clientBonusId,
                    getCJVisualizationReferenceFormConfig(first(rewardClientBonusIdOption.BaseTypes)),
                    rewardClientBonusIdOption,
                    subStep.node,
                    elements,
                );
            }
            isClientBonusIdValid = isReference && subStep.node.data.errorMessages.length === 0;
        }
    }

    reward.isValid = isConditionValid && isClientBonusIdValid && cjVisualizationTemplatesValidation(reward.templates);
    return reward.isValid;
}

function cjWfDateTimeValidation(val, node, option, config, elements) {
    const model = config.mapToUIModel(val);
    if (
        model?.value?.template === dynamicDateData.sameDay.value ||
        model?.value?.template === dynamicDateData.daysLater.value
    ) {
        const blockId = model?.value?.params?.property?.parentValue;
        yupSchemaValidate(
            CJReferenceOptionValidationSchema(
                elements,
                node,
                config.optionType,
                option.ModelRelationScope,
                blockId === node.id
                    ? {
                          typeName: option?.SelfModelTypeName,
                          properties: option?.SelfModelProperties?.Properties,
                      }
                    : {},
            ),
            {
                blockId: blockId,
                propertyName: model?.value?.params?.property?.value,
                logicFunction: model?.value?.params?.property?.logicFunction,
                argumentInType: CJArgumentValuesTypes.ReferenceInArgument,
            },
            cjReferencePropertySchemaErrorMessageCreator(node),
        );
    }
}

function cjWfGuidValidation(val, node, option, config, elements) {
    if (option?.Required === false) {
        return;
    }
    const model = config.mapToUIModel(val);
    yupSchemaValidate(
        CJReferenceOptionValidationSchema(
            elements,
            node,
            config.optionType,
            option.ModelRelationScope,
            model?.blockId === node.id
                ? {
                      typeName: option?.SelfModelTypeName,
                      properties: option?.SelfModelProperties?.Properties,
                  }
                : {},
        ),
        model,
        cjReferencePropertySchemaErrorMessageCreator(node),
    );
}

function cjWfBlockNameValidation(val, node, option, config, elements) {
    if (option?.Required === false) {
        // Case never possible
        // TODO: when option is optional need discuss, its same when in filter condition property is optional (non required)
        return;
    }
    const model = config.mapToUIModel(val);
    yupSchemaValidate(
        CJBlockNameOptionValidationSchema(elements, node),
        model,
        cjReferencePropertySchemaErrorMessageCreator(node),
    );
}

function optionApiModelValueValidation(val, config, option, node, elements) {
    if (val?.$type !== CJReferenceInArgumentType && val?.$type !== CJFormulaInArgumentType) {
        if (!isNil(config.validationSchema)) {
            const validationSchema = config.validationSchema(option, node);

            yupSchemaValidate(validationSchema, config.mapToUIModel(val), cjStaticSchemaErrorMessageCreator(node));
        } else {
            // checking dynamic render components, now only Bonus
            // and all dynamic render components has not validationSchema
            if (config.optionType === formsTypes.WfBonus.optionType) {
                cjWfBonusValidation(node, option, config);
            }
            if (config.optionType === formsTypes.WfDateTime.optionType) {
                cjWfDateTimeValidation(val, node, option, config, elements);
            }
            if (config.optionType === formsTypes.FilterCondition.optionType) {
                cjFilterConditionValidation(elements, node, option, config);
            }
            if (config.optionType === formsTypes.WfGuid.optionType) {
                cjWfGuidValidation(val, node, option, config, elements);
            }
            if (config.optionType === formsTypes.WfBlockName.optionType) {
                cjWfBlockNameValidation(val, node, option, config, elements);
            }
            if (config.optionType === formsTypes.TemplateConfig.optionType) {
                cjTemplateConfigValidation(val, node, option, config, elements);
            }
            if (config.optionType === formsTypes['TemplateMessageConfig[]'].optionType) {
                cjTemplateMessageConfigValidation(val, node, option, config, elements);
            }
        }
    } else {
        // TODO: reference in argument type, move yup validation for validate also in modal
        if (!isEmpty(config) && !isNil(config?.optionType)) {
            const tmpModel = config.mapToUIModel(val);

            let tmpOptionType = config.optionType;
            if (val?.$type === CJFormulaInArgumentType && !isEmpty(tmpModel)) {
                let property: Nullable<AnyObject> = null;
                if (tmpModel?.blockId === node.id) {
                    property = option?.SelfModelProperties?.Properties;
                } else {
                    const block = elements.find((el) => el.id === tmpModel.blockId);
                    if (block?.data?.metaData?.Properties) {
                        property = block?.data?.metaData?.Properties.find(
                            (prop) => prop.Name === tmpModel.propertyName,
                        );
                    }
                }
                if (!isNil(property?.Functions)) {
                    const logicFunction = property?.Functions.find((func) => func?.value === tmpModel.logicFunction);
                    if (!isNil(logicFunction?.returnType?.Name)) {
                        tmpOptionType = isNil(logicFunction?.returnType?.Name)
                            ? ''
                            : last(logicFunction?.returnType?.Name.split('.'));
                    }
                }
            }

            yupSchemaValidate(
                CJReferenceOptionValidationSchema(
                    elements,
                    node,
                    tmpOptionType,
                    option.ModelRelationScope,
                    tmpModel?.blockId === node.id
                        ? {
                              typeName: option?.SelfModelTypeName,
                              properties: option?.SelfModelProperties?.Properties,
                          }
                        : {},
                ),
                tmpModel,
                cjReferencePropertySchemaErrorMessageCreator(node),
            );
        }
    }
}

function customerJourneyOptionsValidation(node, elements) {
    if (!isNil(node?.data?.metaData?.Options)) {
        node.data.metaData.Options.forEach((option) => {
            const config = getFormContent(option.BaseTypes, formsTypes);
            const val = node?.data?.apiModel[option.Name];
            optionApiModelValueValidation(val, config, option, node, elements);
        });
    }
}

function cjFilterConditionValidation(elements, node, option, config) {
    const val = node?.data?.apiModel[option.Name];
    const model = config.mapToUIModel(val);
    // TODO: Filter block must be have minimum one filter
    if (model?.argumentInType !== CJArgumentValuesTypes.DefaultInArgument) {
        if (isNil(model?.defaultSelectedLogic)) {
            // TODO: case does not possible, but need checking for full validation
        }
        if (isNil(model?.filters) || !isArray(model.filters)) {
            if (option.Required) {
                node.data.errorMessages.push({ errorMessage: l.CannotBeEmpty, errorParams: { fieldName: l.Block } });
            }
        } else {
            cjFilterGroupValidation(model, elements, node, option);
        }
    } else if (option.Required) {
        node.data.errorMessages.push({ errorMessage: l.CannotBeEmpty, errorParams: { fieldName: l.Block } });
    }
}

function cjFilterGroupValidation(filter, elements, node, option) {
    if (filter?.isGroupFilter === true) {
        if (!isNil(filter?.filters)) {
            filter.filters.forEach((filter) => {
                cjFilterGroupValidation(filter, elements, node, option);
            });
        }
    } else {
        const leftProperty = {
            blockId: filter?.property?.parentValue,
            propertyName: filter?.property?.value,
            logicFunction: filter?.property?.logicFunction,
        };
        let leftPropertyOption: Nullable<AnyObject> = null;
        if (!isNil(leftProperty.blockId) && !isNil(leftProperty.propertyName)) {
            if (!isNil(option?.SelfModelTypeName) && leftProperty.blockId === node.id) {
                if (!isNil(option?.SelfModelProperties?.Properties)) {
                    leftPropertyOption = option?.SelfModelProperties?.Properties.find(
                        (property) => property.Name === leftProperty.propertyName,
                    );
                }
            } else {
                const tmpBlock = elements.find((el) => el.id === leftProperty.blockId);
                if (!isNil(tmpBlock?.data?.metaData?.Properties)) {
                    leftPropertyOption = tmpBlock.data.metaData.Properties.find(
                        (property) => property.Name === leftProperty.propertyName,
                    );
                }
            }
        }

        if (!isNil(leftProperty?.logicFunction) && !isNil(leftPropertyOption?.Functions)) {
            const tmpFunction = leftPropertyOption?.Functions.find((func) => func.value === leftProperty.logicFunction);
            if (!isNil(tmpFunction)) {
                leftPropertyOption = cloneDeep(leftPropertyOption);
                if (leftPropertyOption) {
                    leftPropertyOption.BaseTypes = tmpFunction.returnType?.BaseTypes;
                }
            }
        }

        const config = getFormContent(leftPropertyOption?.BaseTypes, formsTypes);

        yupSchemaValidate(
            CJReferenceOptionValidationSchema(
                elements,
                node,
                isNil(leftPropertyOption?.BaseTypes)
                    ? ''
                    : last((first(leftPropertyOption?.BaseTypes ?? []) as string).split('.')),
                option.ModelRelationScope,
                leftProperty.blockId === node.id
                    ? {
                          typeName: option?.SelfModelTypeName,
                          properties: option?.SelfModelProperties?.Properties,
                      }
                    : {},
            ),
            leftProperty,
            cjReferencePropertySchemaErrorMessageCreator(node),
        );

        if (isNil(filter.operator)) {
            // TODO: operator is required
        } else {
            // TODO: operator validation with leftProperty must be operators

            // TODO: right value validation
            if (
                !isNil(leftPropertyOption) &&
                !isNil(config?.optionType) &&
                !excludeRightValueContentVisibilitiesOperators.includes(filter.operator)
            ) {
                optionApiModelValueValidation(
                    filter.rightValue,
                    config,
                    {
                        ...leftPropertyOption,
                        ModelRelationScope: option.ModelRelationScope,
                        Required: true,
                        SelfModelTypeName: option?.SelfModelTypeName,
                        SelfModelProperties: option?.SelfModelProperties,
                    },
                    node,
                    elements,
                );
            }
        }
    }
}

function cjWfBonusValidation(node, option, config) {
    const val = node?.data?.apiModel[option.Name];
    const model = config.mapToUIModel(val);
    const validationSchema = CJPartnerBonusIdValidationSchema(option);

    yupSchemaValidate(validationSchema, { value: model?.bonusId }, cjStaticSchemaErrorMessageCreator(node));

    const bonusConfig = JSON.parse(val?.ConfigJson ?? JSON_OBJECT);
    const expiration = bonusConfig.Expiration ?? null;

    if (!isNil(expiration)) {
        yupSchemaValidate(
            CJBonusExpirationValidationSchema(),
            {
                [ExpirationLabels.Type]: ExpirationBonusModelTypes[expiration?.$type] ?? '',
                [ExpirationLabels.Period]: isNil(expiration?.Period) ? 0 : +expiration.Period.split('.')[0],
                [ExpirationLabels.Date]: expiration?.Date,
                [ExpirationLabels.HasExpiration]: true,
            },
            cjStaticSchemaErrorMessageCreator(node),
        );
    }

    if (!isNil(node?.data?.dependencies) && !isNil(node?.data?.dependencies[CJDependencyTypes.Bonus])) {
        const bonus = find(node.data.dependencies[CJDependencyTypes.Bonus], (item) => +item.Id === +model.bonusId);
        // TODO: cannot be case when bonus is null but need when initial request is expired
        if (!isNil(bonus?.BonusDetails)) {
            bonus.BonusDetails.filter(
                (bonusDetail) => isNil(model?.wallet) || bonusDetail.CurrencyCode === model.wallet,
            ).forEach((bonusDetail) => {
                const amount = isNil(model?.amounts)
                    ? null
                    : find(model.amounts, (item) => item.CurrencyCode === bonusDetail.CurrencyCode)?.OriginalValue;
                const bonusAmountValidationSchema = CJBonusAmountInputValidationSchema(bonusDetail, option?.Required);

                yupSchemaValidate(bonusAmountValidationSchema, { amount }, cjStaticSchemaErrorMessageCreator(node));
            });
        }
    }
}

function cjTemplateConfigValidation(val, node, option, config, elements) {
    const model = config.mapToUIModel(val);
    const validationSchema = CJTemplateIdValidationSchema(option);
    yupSchemaValidate(validationSchema, { value: model?.templateId }, cjStaticSchemaErrorMessageCreator(node));
    if (
        !isNil(node?.data?.dependencies) &&
        !isNil(node?.data?.dependencies[CJDependencyTypes.Template]) &&
        !isNil(model?.templateId) &&
        !isNil(model?.dynamicVariables)
    ) {
        const dependencyObject = find(
            node.data.dependencies[CJDependencyTypes.Template],
            (item) => +item.templateId === +model.templateId,
        );

        if (!isEmpty(dependencyObject?.dynamicVariables)) {
            values(dependencyObject.dynamicVariables).forEach((item) => {
                const templateItemValue = model.dynamicVariables[item.fieldName];
                if (!isNil(templateItemValue)) {
                    yupSchemaValidate(
                        CJReferenceOptionValidationSchema(
                            elements,
                            node,
                            item?.wfType,
                            option.ModelRelationScope,
                            templateItemValue?.value?.blockId === node.id
                                ? {
                                      typeName: option?.SelfModelTypeName,
                                      properties: option?.SelfModelProperties?.Properties,
                                  }
                                : {},
                            isNil(item.wfType),
                        ),
                        templateItemValue?.value,
                        cjReferencePropertySchemaErrorMessageCreator(node),
                    );
                }
            });
        }
    }
}

const cjTemplateMessageConfigValidation = (val, node, option, config, elements) => {
    const model = config.mapToUIModel(val);
    const tmpResult = cloneDeep(model);
    delete tmpResult.argumentInType;
    const validationSchema = CJBestMessageSenderValidationSchema(option);
    yupSchemaValidate(validationSchema, { templateMessageConfig: tmpResult }, cjStaticSchemaErrorMessageCreator(node));

    const selectedTemplates = Object.values(model)
        .filter((channelData: Nullable<AnyObject>) => {
            return channelData?.template?.value;
        })
        .map((channelData: AnyObject) => {
            return channelData.template?.value;
        });

    const hasDynamicVariablesInModel = values(model).some((channelData) => {
        return !isEmpty(channelData.dynamicVariables);
    });

    if (
        !isNil(node?.data?.dependencies) &&
        !isNil(node?.data?.dependencies[CJDependencyTypes.TemplateBestChannelTime]) &&
        !isEmpty(selectedTemplates) &&
        hasDynamicVariablesInModel
    ) {
        node.data.dependencies[CJDependencyTypes.TemplateBestChannelTime].forEach((dependencyObject) => {
            values(dependencyObject?.dynamicVariables).forEach((item) => {
                const channelDataWithDynamicVariables = values(model).find((channelData) => {
                    return has(channelData.dynamicVariables, item.fieldName);
                });
                const templateItemValue = channelDataWithDynamicVariables?.dynamicVariables[item.fieldName];

                if (!isNil(templateItemValue)) {
                    yupSchemaValidate(
                        CJReferenceOptionValidationSchema(
                            elements,
                            node,
                            item?.wfType,
                            option.ModelRelationScope,
                            templateItemValue?.value?.blockId === node.id
                                ? {
                                      typeName: option?.SelfModelTypeName,
                                      properties: option?.SelfModelProperties?.Properties,
                                  }
                                : {},
                            isNil(item.wfType),
                        ),
                        templateItemValue?.value,
                        cjReferencePropertySchemaErrorMessageCreator(node),
                    );
                }
            });
        });
    }
};

const expirationTypePeriodValidation = (data) => {
    const { type, period } = data || {};
    return type === ExpirationTypes.ExpirationPeriod && isNil(period);
};

const expirationTypeDateValidation = (data) => {
    const { type, date } = data || {};
    return type === ExpirationTypes.ExpirationDate && isNil(date);
};

const expirationTypeDateTimeValidation = (data) => {
    const { type, time } = data || {};
    return type === ExpirationTypes.ExpirationDate && isNil(time);
};

const expirationTypeDateTimeZoneValidation = (data) => {
    const { type, timezone } = data || {};
    return type === ExpirationTypes.ExpirationDate && isNil(timezone);
};

const viberButtonTextValidation = (data) => {
    const { text, url } = data;
    return isEmpty(text.trim()) && !isEmpty(url.trim());
};

const viberButtonUrlValidation = (data) => {
    const { text, url } = data;
    return isEmpty(url.trim()) && !isEmpty(text.trim());
};

const telegramButtonTextValidation = (data) => {
    const { text, url } = data;
    return isEmpty(text.trim()) && !isEmpty(url.trim());
};

const telegramButtonUrlValidation = (data) => {
    const { text, url } = data;
    return isEmpty(url.trim()) && !isEmpty(text.trim());
};

const isUrlValidOrEmptyString = (data) => {
    const { url } = data;
    if (isEmpty(url)) {
        return false;
    }
    const res = url.match(
        // eslint-disable-next-line
        /(http(s)?:\/\/.)?(www\.)?[-a-zA-Z0-9@:%._\+~#=]{2,256}\.[a-z]{2,6}\b([-a-zA-Z0-9@:%_\+.~#?&//=]*)/g,
    );
    return res === null;
};

const isValidQueryConfigurations = (data) => {
    let hasError = false;
    values(data).forEach((segment) => {
        if (isEmpty(segment.filters) || isEmpty(segment.name)) {
            hasError = true;
        }
    });
    return hasError;
};

const hasDeactivatedSelection = (data) => {
    return data.some((obj) => obj.isUnavailable);
};

const isDeniedTagExist = (value, parametersValues) => {
    const { tags, isPartial } = parametersValues;

    if (isPartial) {
        const specificTags = ['html', 'body', 'head', 'script', 'meta', 'base', 'title'];
        const tagGroup = tags.reduce((tagsString, tag) => {
            if (specificTags.includes(tag)) {
                tagsString += `${tagsString === '' ? '' : '|'}${tag}`;
            }

            return tagsString;
        }, '');
        const isTagsExist = new RegExp(`<(${tagGroup})(.*)>`).test(value);

        if (isTagsExist) return true;
    }

    const htmlDocument = htmlStringToDomElement(value);
    const targetElement = isPartial ? htmlDocument.body : htmlDocument;

    const foundTags = tags.reduce((foundTagsAcc, tag) => {
        const element = targetElement.querySelector(tag);

        if (element) foundTagsAcc.push(element.localName);

        return foundTagsAcc;
    }, []);

    return foundTags.length > 0;
};

const isPositiveInteger = ({ value, type }) => {
    if (isNil(type) || type === ExpirationTypes.ExpirationDate) {
        return false;
    }
    const pattern = new RegExp('^[1-9]\\d*$');

    return value?.match(pattern) === null;
};

const isDateBiggerThanCurrentDate = ({ value, type }) => {
    if (isNil(type) || type === ExpirationTypes.ExpirationPeriod) {
        return false;
    }
    const currentDate = new Date();
    const chosenDate = new Date(value);

    return chosenDate.getTime() <= currentDate.getTime();
};

const isExpirationDateTimeGreaterThanNow = (data) => {
    const { date, time, timezone, type } = data || {};

    if (type !== ExpirationTypes.ExpirationDate || isNil(date) || isEmpty(time) || isNil(timezone)) {
        return false;
    }

    return isBeforeCurrentDate(date, time ?? ZERO_TIME_WITHOUT_SECONDS, timezone);
};

const startTimeValidation = (startTime) => {
    const result = /^([0-2]?[0-9]):[0-5][0-9]$/.test(startTime);
    return !result;
};

const isSelectedWalletSupportedInBonus = ({ selectedWallet, amounts }) => {
    if (isNil(selectedWallet)) {
        return false;
    }
    return !(selectedWallet in amounts);
};

function validationCases({ ValidationType, ParametersValues }, value) {
    switch (ValidationType) {
        case 'required':
            return isRequired(value);
        case 'requiredField':
            return isRequired(value?.[value?.key]);
        case 'isInRange':
            return isInRange(value, ParametersValues);
        case 'requiredEditor':
            return isRequiredEditor(value);
        case 'requiredEmailBuilder':
            return isRequiredEmailBuilder(value);
        case 'deniedTagsNotAllowed':
            return isDeniedTagExist(value, ParametersValues);
        case 'minLength':
            return isLessThen(value, ParametersValues);
        case 'minLengthOptional':
            return !isEmpty(value) && isLessThen(value, ParametersValues);
        case 'maxLength':
            return isMoreThen(value, ParametersValues);
        case 'customExpression':
            return regexMatching(value, ParametersValues);
        case 'greaterThan':
            return isGreaterThan(value, ParametersValues);
        case 'greaterThanOrEqual':
            return isGreaterThanOrEqual(value, ParametersValues);
        case 'lessThan':
            return isLessThan(value, ParametersValues);
        case 'lessThanOrEqual':
            return isLessThanOrEqual(value, ParametersValues);
        case 'email':
            return regexMatching(value, ParametersValues);
        case 'phone':
            return regexMatching(value, ParametersValues);
        case 'maxCountCustomExpression':
            return regexMatching(value, ParametersValues);
        case 'queryConfigurations':
            return isValidQueryConfigurations(value);
        case 'isEmpty':
            return isEmptyValue(value);
        case 'firstSymbol':
            return regexMatching(value, ParametersValues);
        case 'fromDateBiggerThanCurrentDate':
            return (
                !(value.pageType === PageTypesEnum.edit && +value.campaignStatus === +CampaignStatusLabels.Idle) &&
                isBeforeCurrentDate(value.fromDate, value.fromTime, value.timeZone)
            );

        case 'toDateBiggerThanCurrentDate':
            return isBeforeCurrentDate(value.toDate, '23:59', value.timeZone);
        case 'dateCompareThanDate':
            return isDateSmallerThanDate(value.fromDate, value.toDate);
        case 'customerJourney':
            return isValidCustomerJourney(value);
        case 'customerJourneyAvailableBlocksValidation':
            return isAvailableCustomerJourneyBlocks(value);
        case 'isAvailablePushNotificationRoute':
            return isAvailablePushNotificationRoute(value);
        case 'isFileFormatValid':
            return isFileFormatValid(value, ParametersValues);
        case 'requiredVisualizationTemplate':
            return (
                isNil(value?.name) ||
                isEmpty(trim(value.name)) ||
                !yupSchemaValidate(CJVisualizationStepTemplateValidationSchema(), { name: value.name })
            );
        case 'templateCrossValidation': {
            return templateCrossValidation(value, ParametersValues);
        }
        case 'templateCrossValidationViber': {
            return templateCrossValidation(
                { ...value, shortenerAndTrackerSettings: value.shortenerAndTrackerSettings.viber },
                ParametersValues,
            );
        }
        case 'templateCrossValidationWhatsAppProvider': {
            return templateCrossValidation(
                { ...value, shortenerAndTrackerSettings: value.shortenerAndTrackerSettings.whatsAppProvider },
                ParametersValues,
            );
        }
        case 'templateCrossValidationSMS': {
            return templateCrossValidation(
                { ...value, shortenerAndTrackerSettings: value.shortenerAndTrackerSettings.sms },
                ParametersValues,
            );
        }
        case 'templateForbiddenDynamicVariables': {
            return templateForbiddenDynamicVariables(value, ParametersValues);
        }
        case 'whatsAppPlaceholderMappingRequired': {
            return whatsAppPlaceholderMappingRequired(value);
        }
        case 'externalWhatsAppTemplateDetails': {
            return externalWhatsAppTemplateDetails(value);
        }
        case 'maxLengthWithRegEx':
            return isMoreThanRegex(value, ParametersValues);
        case 'customerJourneyLimitValidation':
            return customerJourneyLimitValidation(ParametersValues);
        case 'unique':
            return !isNil(value) && isArray(value) && value.length !== uniq(value).length;
        case 'viberButtonText':
            return viberButtonTextValidation(value);
        case 'viberButtonUrl':
            return viberButtonUrlValidation(value);
        case 'isUrlValidOrEmptyString':
            return isUrlValidOrEmptyString(value);
        case 'viberButtonTextMaxLength':
            return viberButtonTextMaxLength(value, ParametersValues);
        case 'telegramButtonText':
            return telegramButtonTextValidation(value);
        case 'telegramButtonUrl':
            return telegramButtonUrlValidation(value);
        case 'telegramButtonTextMaxLength':
            return telegramButtonTextMaxLength(value, ParametersValues);
        case 'whatsAppProviderButtonText':
            return whatsAppProviderButtonTextValidation(value);
        case 'whatsAppProviderButtonUrl':
            return whatsAppProviderButtonUrlValidation(value);
        case 'whatsAppProviderButtonTextMaxLength':
            return whatsAppProviderButtonTextMaxLength(value, ParametersValues);
        case 'hasDeactivatedSelection':
            return hasDeactivatedSelection(value);
        case 'schedulerUnitPairs':
            return isSchedulerUnitPairsInvalid(value);
        case 'schedulerWeeklyUnit':
            return isSchedulerUnitPairsInvalid(value);
        case 'isPositiveInteger':
            return isPositiveInteger(value);
        case 'isDateBiggerThanCurrentDate':
            return isDateBiggerThanCurrentDate(value);
        case 'expirationDateTimeGreaterThanNow':
            return isExpirationDateTimeGreaterThanNow(value);
        case 'startTimeValidation':
            return startTimeValidation(value);
        case 'isSelectedWalletSupportedInBonus':
            return isSelectedWalletSupportedInBonus(value);
        case 'expirationTypePeriodValidation':
            return expirationTypePeriodValidation(value);
        case 'expirationTypeDateValidation':
            return expirationTypeDateValidation(value);
        case 'expirationTypeDateTimeValidation':
            return expirationTypeDateTimeValidation(value);
        case 'expirationTypeDateTimeZoneValidation':
            return expirationTypeDateTimeZoneValidation(value);

        default:
            return false;
    }
}

// TODO: is not used, need review
function singleValidator(_label, value, validationCredentials, data) {
    const foundValidationCredential = validationCredentials.find((validationCredential) => {
        return validationCases(validationCredential, value);
    });
    if (foundValidationCredential) {
        return {
            ...data,
            isValid: false,
            errorText: foundValidationCredential.ErrorMessage,
            value,
        };
    } else {
        return { ...data, isValid: true, errorText: '', value };
    }
}

// TODO: is not used, need review
function customValidator(data, validateAll, setErrorValidationScenario, errorValidationScenario) {
    const resultValidationScenario = keys(data).reduce((acc, key) => {
        acc[key] =
            validateAll && data[key].isValid
                ? data[key]
                : singleValidator(
                      key,
                      validateAll ? data[key].value : data[key],
                      errorValidationScenario[key].validationCredentials,
                      errorValidationScenario[key],
                  );
        return acc;
    }, {});

    setErrorValidationScenario({ ...errorValidationScenario, ...resultValidationScenario });
}

export {
    customValidator,
    validationCases,
    yupSchemaValidate,
    getReferencePropertyErrorMessage,
    cjVisualizationStepValidation,
    cjVisualizationSubStepUnlockValidation,
};
